import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useCookies } from "react-cookie";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Card,
  CardContent,
  CardHeader,
  Alert,
  FormHelperText,
  Snackbar,
} from "@mui/material";
import {
  Warning as HighRiskIcon,
  Info as MediumRiskIcon,
  CheckCircle as LowRiskIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import Layout from "../components/layout/CompliceLayout";
import axios from "axios";
import config from "../config";

const defaultValues = {
  title: "",
  category: "",
  riskLevel: "",
  impactScore: "",
  probabilityScore: "",
  currentStatus: "New",
  department: "",
  description: "",
  mitigationStrategy: "",
  potentialImpact: [],
};

const RiskLevelChip = ({ level }) => {
  const getChipProps = () => {
    switch (level) {
      case "High":
        return { color: "error", icon: <HighRiskIcon />, severity: "high" };
      case "Medium":
        return {
          color: "warning",
          icon: <MediumRiskIcon />,
          severity: "medium",
        };
      case "Low":
        return { color: "success", icon: <LowRiskIcon />, severity: "low" };
      default:
        return { color: "default" };
    }
  };
  return (
    <Chip label={level} {...getChipProps()} variant="outlined" size="small" />
  );
};

const CompliceRiskIdentification = () => {
  const [risks, setRisks] = useState([]);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [filters, setFilters] = useState({ category: "", riskLevel: "" });
  const { API_BASE_URL } = config;
  const [cookies] = useCookies(["userId"]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues,
  });

  const showToast = (message, severity = "success") => {
    setToast({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseToast = (event, reason) => {
    if (reason === "clickaway") return;
    setToast((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    fetchRisks();
  }, []);



  const fetchRisks = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/risks/${cookies.userId}`
      );

     

      // API returns { success, message, data } structure
      const risksData = response.data?.data || [];
   
      
      const safeRisks = Array.isArray(risksData) ? risksData : [];
      
      setRisks(safeRisks);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Failed to fetch risks";
      setError(errorMessage);
      showToast(errorMessage, "error");
      console.error("Error fetching risks:", err);
      // Set empty array on error to prevent issues
      setRisks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRisk = async (data) => {
    try {
      setLoading(true);
      setError(null);

      const payload = {
        userId: cookies.userId,
        ...data,
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/v1/risks`,
        payload
      );

      if (response.status === 201) {
        await fetchRisks();
        handleCloseForm();
        showToast("Risk created successfully");
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.error || "Failed to create risk.";
      setError(errorMessage);
      showToast(errorMessage, "error");
      console.error("Error creating risk:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRisk = async (data) => {
    if (!selectedRisk?.id) {
      setError("No risk selected for update");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await axios.put(
        `${API_BASE_URL}/api/v1/risks/${selectedRisk.id}`,
        {
          ...data,
          userId: cookies.userId,
        }
      );

      if (response.status === 200) {
        await fetchRisks();
        handleCloseForm();
        showToast("Risk updated successfully");
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Failed to update risk";
      setError(errorMessage);
      showToast(errorMessage, "error");
      console.error("Error updating risk:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRisk = async (riskId, event) => {
    event.preventDefault();
    event.stopPropagation();

    
    if (isDeleting || !riskId) return;

    if (!window.confirm("Are you sure you want to delete this risk?")) {
      return;
    }

    try {
      setIsDeleting(true);
      setError(null);

      const response = await axios.delete(
        `${API_BASE_URL}/api/v1/risks/${riskId}`
      );

      if (response.status === 200) {
        setRisks((prevRisks) => prevRisks.filter((risk) => risk.id !== riskId));

        if (selectedRisk?.id === riskId) {
          setSelectedRisk(null);
          setIsFormOpen(false);
        }

        showToast("Risk deleted successfully");
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Failed to delete risk";
      setError(errorMessage);

      showToast(errorMessage, "error");
      console.error("Error deleting risk:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenForm = (risk = null, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (risk) {
      setSelectedRisk(risk);
      setIsEditing(true);
      reset({
        ...risk,
        potentialImpact: Array.isArray(risk.potentialImpact)
          ? risk.potentialImpact
          : risk.potentialImpact?.split(",").map((item) => item.trim()) || [],
      });
    } else {
      setSelectedRisk(null);
      setIsEditing(false);
      reset(defaultValues);
    }
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedRisk(null);
    setIsEditing(false);
    reset(defaultValues);
  };

  const handleCloseDetails = () => {
    setSelectedRisk(null);
    setError(null);
  };

  const onSubmit = (data) => {
    if (isEditing) {
      handleUpdateRisk(data);
    } else {
      handleCreateRisk(data);
    }
  };

  const calculateRiskScore = (risk) => {
    return ((risk.impactScore * risk.probabilityScore) / 100).toFixed(2);
  };

  // Ensure risks is always an array before filtering
  const filteredRisks = Array.isArray(risks) ? risks.filter((risk) => {
    const matchesCategory = filters.category
      ? risk.category === filters.category
      : true;
    const matchesRiskLevel = filters.riskLevel
      ? risk.riskLevel === filters.riskLevel
      : true;
    return matchesCategory && matchesRiskLevel;
  }) : [];

  const handleRowClick = (risk, event) => {
    if (event.target.closest("button")) {
      return;
    }
    setSelectedRisk(risk);
  };

  // Safe calculations for risk statistics
  const totalRisks = Array.isArray(risks) ? risks.length : 0;
  const highRisks = Array.isArray(risks) ? risks.filter((r) => r.riskLevel === "High").length : 0;
  const mediumRisks = Array.isArray(risks) ? risks.filter((r) => r.riskLevel === "Medium").length : 0;

  return (
    <Layout>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Risk Identification & Management
        </Typography>

        {/* Risk Form Dialog */}
        <Dialog
          open={isFormOpen}
          onClose={handleCloseForm}
          maxWidth="md"
          fullWidth
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogTitle>
              {isEditing ? "Edit Risk" : "Create New Risk"}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Controller
                    name="title"
                    control={control}
                    rules={{ required: "Title is required" }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Risk Title"
                        fullWidth
                        error={!!errors.title}
                        helperText={errors.title?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="category"
                    control={control}
                    rules={{ required: "Category is required" }}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.category}>
                        <InputLabel>Category</InputLabel>
                        <Select {...field} label="Category">
                          <MenuItem value="IT Security">IT Security</MenuItem>
                          <MenuItem value="Operational">Operational</MenuItem>
                          <MenuItem value="Compliance">Compliance</MenuItem>
                        </Select>
                        <FormHelperText>
                          {errors.category?.message}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="riskLevel"
                    control={control}
                    rules={{ required: "Risk Level is required" }}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.riskLevel}>
                        <InputLabel>Risk Level</InputLabel>
                        <Select {...field} label="Risk Level">
                          <MenuItem value="High">High</MenuItem>
                          <MenuItem value="Medium">Medium</MenuItem>
                          <MenuItem value="Low">Low</MenuItem>
                        </Select>
                        <FormHelperText>
                          {errors.riskLevel?.message}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="impactScore"
                    control={control}
                    rules={{
                      required: "Impact Score is required",
                      min: {
                        value: 0,
                        message: "Score must be between 0 and 10",
                      },
                      max: {
                        value: 10,
                        message: "Score must be between 0 and 10",
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Impact Score (0-10)"
                        type="number"
                        fullWidth
                        error={!!errors.impactScore}
                        helperText={errors.impactScore?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="probabilityScore"
                    control={control}
                    rules={{
                      required: "Probability Score is required",
                      min: {
                        value: 0,
                        message: "Score must be between 0 and 10",
                      },
                      max: {
                        value: 10,
                        message: "Score must be between 0 and 10",
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Probability Score (0-10)"
                        type="number"
                        fullWidth
                        error={!!errors.probabilityScore}
                        helperText={errors.probabilityScore?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="description"
                    control={control}
                    rules={{ required: "Description is required" }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Description"
                        multiline
                        rows={3}
                        fullWidth
                        error={!!errors.description}
                        helperText={errors.description?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="mitigationStrategy"
                    control={control}
                    rules={{ required: "Mitigation Strategy is required" }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Mitigation Strategy"
                        multiline
                        rows={4}
                        fullWidth
                        error={!!errors.mitigationStrategy}
                        helperText={errors.mitigationStrategy?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="department"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Department"
                        fullWidth
                        error={!!errors.department}
                        helperText={errors.department?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="potentialImpact"
                    control={control}
                    defaultValue={[]}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Potential Impact (comma-separated)"
                        fullWidth
                        multiline
                        rows={2}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value.split(",").map((item) => item.trim())
                          )
                        }
                        value={Array.isArray(field.value) ? field.value.join(", ") : ""}
                        helperText="Enter potential impacts separated by commas"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="currentStatus"
                    control={control}
                    rules={{ required: "Status is required" }}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.currentStatus}>
                        <InputLabel>Current Status</InputLabel>
                        <Select {...field} label="Current Status">
                          <MenuItem value="New">New</MenuItem>
                          <MenuItem value="In Progress">In Progress</MenuItem>
                          <MenuItem value="Assessed">Assessed</MenuItem>
                          <MenuItem value="Monitored">Monitored</MenuItem>
                          <MenuItem value="Resolved">Resolved</MenuItem>
                        </Select>
                        <FormHelperText>
                          {errors.currentStatus?.message}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseForm}>Cancel</Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
              >
                {loading ? "Saving..." : isEditing ? "Update" : "Create"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Risk Overview Cards */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardHeader title="Total Risks" />
              <CardContent>
                <Typography variant="h4" color="primary">
                  {totalRisks}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  Risks Identified
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardHeader title="Risk Distribution" />
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2">High Risk</Typography>
                  <Typography variant="body2">{highRisks}</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={totalRisks > 0 ? (highRisks / totalRisks) * 100 : 0}
                  color="error"
                  sx={{ mb: 2 }}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2">Medium Risk</Typography>
                  <Typography variant="body2">{mediumRisks}</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={totalRisks > 0 ? (mediumRisks / totalRisks) * 100 : 0}
                  color="warning"
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardHeader title="Risk Management" />
              <CardContent>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  fullWidth
                  onClick={() => handleOpenForm()}
                >
                  Add New Risk
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filtering Section */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Risk Category</InputLabel>
              <Select
                value={filters.category}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, category: e.target.value }))
                }
                label="Risk Category"
              >
                <MenuItem value="">All Categories</MenuItem>
                <MenuItem value="IT Security">IT Security</MenuItem>
                <MenuItem value="Operational">Operational</MenuItem>
                <MenuItem value="Compliance">Compliance</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Risk Level</InputLabel>
              <Select
                value={filters.riskLevel}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, riskLevel: e.target.value }))
                }
                label="Risk Level"
              >
                <MenuItem value="">All Levels</MenuItem>
                <MenuItem value="High">High</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="Low">Low</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Risks Table */}
        <Paper elevation={3}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Risk Title</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Risk Level</TableCell>
                  <TableCell>Risk Score</TableCell>
                  <TableCell>Current Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredRisks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No risks found. Click "Add New Risk" to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRisks.map((risk) => (
                    <TableRow
                      key={risk.id}
                      onClick={(e) => handleRowClick(risk, e)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>{risk.title}</TableCell>
                      <TableCell>{risk.category}</TableCell>
                      <TableCell>
                        <RiskLevelChip level={risk.riskLevel} />
                      </TableCell>
                      <TableCell>{calculateRiskScore(risk)}</TableCell>
                      <TableCell>{risk.currentStatus}</TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedRisk(risk);
                            }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenForm(risk, e)}
                            disabled={loading}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={(e) => handleDeleteRisk(risk.id, e)}
                            color="error"
                            disabled={isDeleting || loading}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Risk Details Dialog */}
        <Dialog
          open={!!selectedRisk && !isFormOpen}
          onClose={handleCloseDetails}
          maxWidth="md"
          fullWidth
        >
          {selectedRisk && (
            <>
              <DialogTitle>
                {selectedRisk.title} - Risk Details
                <Typography variant="subtitle2" color="text.secondary">
                  Identified on{" "}
                  {new Date(selectedRisk.createdAt).toLocaleDateString()}
                </Typography>
              </DialogTitle>
              <DialogContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1">
                      <strong>Risk Level:</strong>{" "}
                      <RiskLevelChip level={selectedRisk.riskLevel} />
                    </Typography>
                    <Typography variant="subtitle1">
                      <strong>Impact Score:</strong> {selectedRisk.impactScore}
                      /10
                    </Typography>
                    <Typography variant="subtitle1">
                      <strong>Probability Score:</strong>{" "}
                      {selectedRisk.probabilityScore}/10
                    </Typography>
                    <Typography variant="subtitle1">
                      <strong>Risk Score:</strong>{" "}
                      {calculateRiskScore(selectedRisk)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1">
                      <strong>Department:</strong> {selectedRisk.department}
                    </Typography>
                    <Typography variant="subtitle1">
                      <strong>Current Status:</strong>{" "}
                      {selectedRisk.currentStatus}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1">
                      <strong>Description:</strong>
                    </Typography>
                    <Typography variant="body1">
                      {selectedRisk.description}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1">
                      <strong>Mitigation Strategy:</strong>
                    </Typography>
                    <Typography
                      variant="body2"
                      component="pre"
                      sx={{
                        whiteSpace: "pre-wrap",
                        backgroundColor: "#f4f4f4",
                        p: 2,
                        borderRadius: 1,
                      }}
                    >
                      {selectedRisk.mitigationStrategy}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1">
                      <strong>Potential Impact:</strong>
                    </Typography>
                    {Array.isArray(selectedRisk.potentialImpact) ? (
                      selectedRisk.potentialImpact.map((impact, index) => (
                        <Chip
                          key={index}
                          label={impact}
                          variant="outlined"
                          sx={{ m: 0.5 }}
                        />
                      ))
                    ) : (
                      <Typography>No potential impacts listed</Typography>
                    )}
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDetails} color="primary">
                  Close
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* Toast/Snackbar for notifications */}
        <Snackbar
          open={toast.open}
          autoHideDuration={4000}
          onClose={handleCloseToast}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseToast}
            severity={toast.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default CompliceRiskIdentification;
